import type { AdsAccount } from '@/hooks/useAdsPerformance';

/**
 * Darf dieses Google-Ads-Konto per Klick zum berichteten Konto werden?
 * (UX-Audit A8G-01, Sofort-Schutz nach D14.)
 *
 * `set_active_google_ads_account` deaktiviert jedes andere aktive Konto
 * DESSELBEN Google-Kontos. Unter google@oma.ag sind das die Rollout-Konten des
 * Messfensters. Deshalb:
 *  - kein aktives Geschwister: frei, ein Klick (Erstwahl eines Kunden);
 *  - aktive Geschwister, keins im Rollout: Rueckfrage mit Namen;
 *  - ein aktives Geschwister im Rollout: gesperrt (der Server sperrt ebenfalls);
 *  - Rollout-Pruefung laeuft noch: gesperrt, neutraler Hinweis, KEIN Fehlertext
 *    (beim Seitenaufbau unter google@oma.ag stuenden sonst rund 149 Fehlermeldungen);
 *  - Rollout-Pruefung gescheitert: gesperrt mit Fehlertext, fail-closed.
 */
export type RolloutKnowledge = ReadonlySet<string> | 'loading' | 'error';

export type SelectLock =
  | { kind: 'free' }
  | { kind: 'confirm'; siblings: AdsAccount[] }
  | { kind: 'locked-rollout'; siblings: AdsAccount[] }
  | { kind: 'locked-checking'; siblings: AdsAccount[] }
  | { kind: 'locked-unknown'; siblings: AdsAccount[] };

export function selectLockFor(
  account: AdsAccount,
  accounts: readonly AdsAccount[],
  rollout: RolloutKnowledge,
): SelectLock {
  const siblings = accounts.filter(
    (a) => a.google_account_id === account.google_account_id && a.id !== account.id && a.is_active,
  );
  if (siblings.length === 0) return { kind: 'free' };
  if (rollout === 'loading') return { kind: 'locked-checking', siblings };
  if (rollout === 'error') return { kind: 'locked-unknown', siblings };
  if (siblings.some((s) => rollout.has(s.id))) return { kind: 'locked-rollout', siblings };
  return { kind: 'confirm', siblings };
}

/** Der Server-Riegel aus Migration 20260927100300 (`ROLLOUT_SIBLING_PROTECTED`). */
export function isRolloutProtectedError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = (error as { message?: unknown }).message;
  return typeof message === 'string' && message.includes('ROLLOUT_SIBLING_PROTECTED');
}
